import {useState, useLayoutEffect, useEffect} from 'react';

import {insertScript, isScriptAlreadyAdded} from '@optimaxdev/utils';

import {logWithSentry} from 'libs/sentry/sentry';

type SettingsType = {
    remount?: boolean;
};

/**
 * Hoc which provide
 *
 * @param {String} scriptUrl url which should load
 * @param {SettingsType} settings it's a settings
 * @returns {Boolean} status of the script is loaded
 */
export const useLoadScript = (scriptUrl: string, {remount = false}: SettingsType = {}) => {
    const [isLoaded, setLoad] = useState(() => isScriptAlreadyAdded(scriptUrl));

    useLayoutEffect(() => {
        insertScript(scriptUrl, remount, () => setLoad(true));
    }, []);

    return isLoaded;
};

/**
 * Hook which provide script loading with checking it by interval
 * @param {String} scriptUrl url which should load
 * @param {String} windowKeyToCheck it's a window key to check script loaded
 * @param {SettingsType} settings it's a settings
 * @returns {Boolean} status of the script is loaded
 */
export const useLoadScriptWithCheck = (
    scriptUrl: string,
    windowKeyToCheck: string,
    {remount = false}: SettingsType = {},
) => {
    const [isLoaded, setLoad] = useState(false);

    useEffect(() => {
        const script = insertScript(scriptUrl, remount, () => {
            setLoad(true);
        });

        const timer = setInterval(() => {
            if (typeof (window as any)[windowKeyToCheck] !== 'undefined') {
                setLoad(true);
                clearInterval(timer);
            }
        }, 1000);

        return () => {
            script.onload = null;
            clearInterval(timer);
        };
    }, []);

    useEffect(() => {
        if (isLoaded && !(window as any)[windowKeyToCheck]) {
            logWithSentry(new Error(`${windowKeyToCheck} Script added to DOM but didn't loaded`), {
                isScriptAlreadyAdded: isScriptAlreadyAdded(scriptUrl),
                scriptUrl,
            });
        }
    }, [isLoaded]);

    return isLoaded;
};
